// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A read-only drawer component for viewing serials.
 * Displays serials with search and filter capabilities,
 * but without the ability to add or remove serials.
 */
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  ArrowBackOutlined,
  CheckOutlined,
  SearchOutlined,
} from '@/constants/hardfin-standard-icons';
import { formatNumber } from '@/utilities/number';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

type FilterType = 'all' | 'valid' | 'invalid';

interface Props {
  /**
   * Function to call when the drawer should be closed.
   */
  onClose: () => void;
  /**
   * The SKU of the part - used as unique identifier.
   */
  partSku: string;
  /**
   * The name of the part.
   */
  partName: string;
  /**
   * The list of serials to display.
   */
  serials: string[];
  /**
   * Set of serials that have been validated as invalid by the backend.
   */
  invalidSerials?: Set<string>;
}

/**
 * A read-only drawer for viewing serials.
 * Features:
 * - Search and filter serials
 * - View invalid serials
 * - No ability to add or remove serials
 */
export function ViewSerialsDrawer(props: Props) {
  const { serials, invalidSerials } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');

  // Combined invalid serials
  const allInvalidSerials = useMemo(() => {
    return invalidSerials || new Set<string>();
  }, [invalidSerials]);

  // Filter serials based on search query and filter type
  const filteredSerials = useMemo(
    () =>
      serials.filter((serial) => {
        const matchesSearch = serial
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        if (filter === 'valid') {
          return matchesSearch && !allInvalidSerials.has(serial);
        }
        if (filter === 'invalid') {
          return matchesSearch && allInvalidSerials.has(serial);
        }
        return matchesSearch;
      }),
    [serials, searchQuery, filter, allInvalidSerials]
  );

  const validCount = useMemo(
    () => serials.filter((s) => !allInvalidSerials.has(s)).length,
    [serials, allInvalidSerials]
  );

  const invalidCount = allInvalidSerials.size;

  return (
    <Paper
      elevation={3}
      square
      sx={{
        width: 500,
        p: 3,
        height: '100%',
        overflowY: 'auto',
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      <Stack gap={2}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Serials for {props.partSku}</Typography>
          <IconButton onClick={props.onClose} size="small">
            <ArrowBackOutlined />
          </IconButton>
        </Stack>

        {/* Summary */}
        <Box>
          <Typography variant="body2" color="text.secondary">
            {props.partName}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Total serials:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(serials.length)}
            </Typography>
          </Stack>
        </Box>

        {/* Filters and search */}
        <Stack direction="row" gap={2} alignItems="center">
          <Box sx={{ width: '66%' }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, newFilter) => {
                if (newFilter !== null) {
                  setFilter(newFilter);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="all">
                <Typography variant="button" textTransform="none">
                  All ({formatNumber(serials.length)})
                </Typography>
              </ToggleButton>
              <ToggleButton value="valid">
                <Typography variant="button" textTransform="none">
                  Valid ({formatNumber(validCount)})
                </Typography>
              </ToggleButton>
              <ToggleButton value="invalid">
                <Typography variant="button" textTransform="none">
                  Invalid ({formatNumber(invalidCount)})
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ width: '33%' }}>
            <TextField
              fullWidth
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>

        {/* Serial list */}
        <Box>
          {filteredSerials.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                minHeight: '20vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No serials found
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 2,
                maxHeight: filteredSerials.length > 30 ? '400px' : 'auto',
                overflowY: filteredSerials.length > 30 ? 'auto' : 'visible',
              }}
            >
              {filteredSerials.map((serial) => {
                const isValid = !allInvalidSerials.has(serial);
                return (
                  <Stack
                    key={serial}
                    direction="row"
                    alignItems="center"
                    gap={1}
                    sx={{
                      borderLeft: 3,
                      borderRadius: '5px',
                      borderColor: isValid ? 'success.main' : 'error.main',
                      pl: 1,
                      py: 0.5,
                      minWidth: 0,
                    }}
                  >
                    {isValid && (
                      <CheckOutlined
                        fontSize="small"
                        sx={{ color: 'success.main', flexShrink: 0 }}
                      />
                    )}
                    <ConditionalTooltip
                      tooltipText={serial.length > 11 ? serial : undefined}
                      arrow
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {serial}
                      </Typography>
                    </ConditionalTooltip>
                  </Stack>
                );
              })}
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
